<template>
  <div class="product-gallery critical-component-clear">
    <div
      v-if="badge?.label"
      class="product-gallery__badge text-label-s"
      :class="{
        ['product-gallery__badge--sale']: badge.type === 'sale'
      }"
      v-text="badge.label"
    />

    <div
      v-if="product"
      class="product-gallery__carousel-container"
    >
      <!-- Display the product gallery carousel on mobile and quickadd -->
      <div
        v-if="showGallery && productImages"
        ref="carousel"
        class="product-gallery__carousel splide"
        :js-carousel="galleryNamespace"
      >
        <div class="splide__track">
          <ul class="splide__list">
            <li
              v-for="(image, index) in productImages"
              :key="`${image.id}-${index}-${image.variant}`"
              class="product-gallery__slide splide__slide"
              @click="handleImageClick(index)"
            >
              <art-direction
                :image="image"
                :padding="false"
              />
            </li>
          </ul>
        </div>

        <!-- Carousel Arrows -->
        <div class="product-gallery__arrows splide__arrows">
          <button
            class="splide__arrow splide__arrow--prev"
            :aria-label="$string('accessibility.carousel.previous')"
          >
            <icon-chevron-left />
          </button>

          <button
            class="splide__arrow splide__arrow--next"
            :aria-label="$string('accessibility.carousel.next')"
          >
            <icon-chevron-right />
          </button>
        </div>
      </div>

      <div
        v-else-if="productImages"
        :key="gridKey"
        class="product-gallery__grid grid"
      >
        <button
          v-for="(image, index) in productImages"
          :key="index"
          class="col l6 product-gallery__grid-image"
          @click="handleImageClick(index)"
        >
          <span
            class="visually-hidden"
            v-text="image.alt || $string('accessibility.image_placeholder')"
          />
          <art-direction
            block-class="product-gallery"
            :image="image"
          />

          <div
            v-if="modelDescriptionText && index === 1"
            class="product-gallery__desktop-description"
          >
            <span
              class="text-body-2xs"
              v-text="modelDescriptionText"
            />
          </div>
        </button>
      </div>
    </div>

    <div
      v-else
      class="product-gallery__carousel-container"
    >
      <!-- Display the product gallery carousel on mobile -->
      <div
        v-if="showGallery"
        ref="carousel"
        class="product-gallery__carousel splide"
        :js-carousel="galleryNamespace"
      >
        <div class="splide__track">
          <ul class="splide__list">
            <li
              v-for="(image, index) in numberOfImages"
              :key="index"
              class="product-gallery__slide splide__slide"
            >
              <div class="main-product__image-placeholder" />
            </li>
          </ul>
        </div>
      </div>

      <div
        v-else
        class="product-gallery__grid grid"
      >
        <button
          v-for="(image, index) in numberOfImages"
          :key="index"
          class="col l6 product-gallery__grid-image"
          :aria-label="$string('accessibility.image_placeholder')"
        >
          <div class="main-product__image-placeholder" />
        </button>
      </div>
    </div>

    <div
      v-if="modelDescriptionText && isMobile"
      class="product-gallery__mobile-description"
    >
      <span
        class="text-body-2xs"
        v-text="modelDescriptionText"
      />
    </div>

    <overlay
      key="productGalleryModal"
      block-class="product-gallery-modal"
      direction="up"
      namespace="productGalleryModal"
      type="modal"
      :template="false"
    >
      <template #body="{ isActive }">
        <product-gallery-modal
          v-if="getComponentVisible('product-gallery-modal')"
          v-cloak
          :current-index="state.slideIndex"
          :images="productImages"
          :is-active="isActive"
          @update-gallery-index="handleZoomGalleryIndexUpdate"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Product gallery (product-gallery)
 * -----------------------------------------------------------------------------
 * Base component for the Product Gallery carousel. Will house the Zoom viewer and 3D Explorer.
 *
 * @param {Object} additionalImage - Setting with placeholder image to make up to even image numbers.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} namespace - Unique namespace for the Gallery.
 * @param {String} modelDescription - String describing the model in pdp images.
 * @param {Object|Boolean} product - CANVAS formatted product object.
 * @param {Object|Boolean} variant - CANVAS formatted variant object.
 *
 */
import { defineAsyncComponent, nextTick } from 'vue'
import { mapActions, mapState } from 'vuex'

import Carousel from '~/api/carousel'

import ArtDirection from '~global/images/art-direction'

import { generateProductBadge } from '~/helpers/format-algolia'

import IconChevronLeft from '~icons/direction/chevron/left.svg'
import IconChevronRight from '~icons/direction/chevron/right.svg'

export default {
  name: 'ProductGallery',

  components: {
    ArtDirection,
    IconChevronLeft,
    IconChevronRight,
    ProductGalleryModal: defineAsyncComponent({
      loader: () => import(
        /* webpackPrefetch: true */
        '~async/product-gallery-modal/product-gallery-modal'
      ),
    }),
  },

  props: {
    additionalImage: {
      type: Object,
      default: () => {},
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    modelDescription: {
      type: String,
      default: '',
    },
    namespace: {
      type: String,
      default: 'ProductGallery',
    },
    numberOfImages: {
      type: Number,
      default: 6,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  data() {
    return {
      carousel: false,
      carouselConfig: {
        classes: {
          pagination: 'splide__pagination product-gallery__pagination',
          page: 'splide__pagination__page product-gallery__page',
        },
        pagination: true,
        rewind: true,
        speed: 800,
        type: 'fade',
      },
      gridKey: 0,
      state: {
        slideIndex: 0,
      },
    }
  },

  computed: {

    /**
     * Compute badge.
     * @returns {Object}
     */
    badge() {
      return generateProductBadge(this.product)
    },

    /**
     * Remove alt-tag image from product images.
     * @returns {Boolean, Array}
     */
    productImagesWithoutAlt() {
      return this.product?.images?.filter((image) => {
        return !image.alt?.includes('alt_landing_image')
      })
    },

    /**
     * Adds the additional image to the image array if product.images is odd.
     * @returns {Boolean, Array}
     */
    productImages() {
      if(!this.productImagesWithoutAlt) {
        return false
      }

      if(this.productImagesWithoutAlt.length % 2 === 0) {
        return this.product.images
      }

      if (!this.additionalImage.src) {
        return [...this.productImagesWithoutAlt]
      }

      return [...this.productImagesWithoutAlt, this.additionalImage]
    },

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      screenHeight: (state) => state.index.screen.height,
      screenWidth: (state) => state.index.screen.width,
    }),

    /**
     * Calculate if there should be a fixed height on the carousel.
     * - Mobile PDP: Gallery should be capped to still show description
     * - Desktop PDP: Gallery should be uncapped and fill height of container
     * @returns {String|Boolean}
     */
    fixedHeight() {
      return this.isMobile ? `${this.screenWidth / 0.8}px` : false
    },

    /**
     * Calculate a unique namespace for the Gallery.
     * @returns {String}
     */
    galleryNamespace() {
      return `${this.namespace}-Gallery`
    },

    /**
     * Calculate whether to show carousel arrows.
     * - Only display on quick add.
     * @returns {Boolean}
     */
    showArrows() {
      return Boolean(!this.isMobile && this.namespace === 'QuickView')
    },

    /**
     * Calculate if images exist and if to show the gallery.
     * @returns {Boolean}
     */
    showGallery() {
      return Boolean(this.isMobile || this.$parent.$options.name === 'QuickView')
    },

    /**
     * Returns the model description from API field or liquid as a fallback.
     * @returns {String|Boolean}
     */
    modelDescriptionText() {
      const description = this.product?.metafields?.find(field => field.name === 'model_description')

      if (description?.value) {
        return description.value
      }

      if (this.modelDescription) {
        return this.modelDescription
      }

      return false
    },
  },

  watch: {

    /**
     * Watch the product object.
     * - Ensure `immediate` is true to allow carousel mount within Quick Form.
     */
    product: {
      handler() {
        this.gridKey++
        if (!this.product) {
          return
        }

        nextTick(() => {
          if (
            !this.$refs.carousel ||
            !this.product?.images.length
          ) {
            return
          }

          this.carousel = Carousel(
            `[js-carousel="${this.galleryNamespace}"]`, {
              ...this.carouselConfig,
              fixedHeight: this.fixedHeight,
              arrows: this.showArrows,
            },
          )

          /**
           * Component initiates.
           */
          this.carousel.mount()
          this.carousel.refresh()
        })
      },
      immediate: true,
    },

    /**
     * Watch the active variant.
     * - Indicates that the PDP form has updated.
     */
    variant: {
      handler() {
        if (
          !this.variant ||
          !this.$refs.carousel ||
          !this.product?.images.length)
        {
          return
        }

        /**
         * Find the relevant image index by the active variant id.
         */
        const variantIndex = this.product.images.findIndex((image) => {
          return Number(image.id) === Number(this.variant.image)
        })

        if (variantIndex < 0 || variantIndex > this.product.images.length) {
          return
        }

        if (typeof this.carousel === 'boolean') {
          return
        }

        this.carousel.go(variantIndex)
      },
      deep: true,
    },
  },

  beforeUnmount() {

    /**
     * Before unmount, destroy the Splide instance.
     * - Required for toggling between Quick View.
     */
    if (typeof this.carousel == 'boolean') {
      return
    }

    this.carousel.destroy()
    this.carousel = false
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * The user has modified the active slide in the 'Zoom modal'.
     * - Update the index in the inline gallery to keep in sync.
     */
    handleZoomGalleryIndexUpdate(index) {
      if(!this.carousel) {
        return
      }
      this.carousel.go(Number(index))
    },

    /**
     * Handles the opening of the zoom modal.
     */
    handleImageClick(index) {
      if (this.namespace === 'QuickView') {
        return
      }

      this.state.slideIndex = index
      this.openOverlay({
        component: 'product-gallery-modal',
        ignoreDismissed: true,
        namespace: 'productGalleryModal',
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-gallery';
</style>
